import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router';
import { CustomerPicker } from '../components/Customers.tsx';
import { Icon, type IconName } from '../components/Icon.tsx';
import { Shell } from '../components/Shell.tsx';
import { Button, Dialog, EmptyState, Field, IconButton, Skeleton, StatusBadge, Switch, type Tone } from '../components/ui.tsx';
import { t, tn, type MessageKey } from '../i18n/index.ts';
import { action, subscribe } from '../lib/api.ts';
import { dateTime, since } from '../lib/format.ts';
import { useSession } from '../lib/session.tsx';
import { useUi } from '../lib/ui-state.tsx';
import { useAction, useSave } from './manage/common.tsx';
import { Kpi, Table } from './Reports.tsx';

/**
 * Calls: the business's phone through its own Twilio number. "Call" rings your own phone first
 * and then the customer, from the business's number. Calls to the business ring everyone who is
 * available; missed calls and voicemails wait under "To call back" until someone does.
 */

type Tab = 'calls' | 'team' | 'settings';
type Dir = 'in' | 'out';
type CState = 'ringing' | 'in_progress' | 'completed' | 'missed' | 'voicemail' | 'no_answer' | 'busy' | 'failed' | 'cancelled';
const FILTERS = ['all', 'call_back', 'in', 'out', 'mine'] as const;
type Filter = (typeof FILTERS)[number];
const DAY_KEYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'] as const;
const LANGUAGES = ['en-GB', 'en-US', 'it-IT', 'de-DE', 'fr-FR', 'es-ES', 'el-GR', 'tr-TR'] as const;

interface CallRow {
  id: string;
  direction: Dir;
  state: CState;
  phone: string;
  customer: string | null;
  customer_name: string | null;
  agent: string | null;
  agent_name: string | null;
  started_at: string;
  answered_at: string | null;
  ended_at: string | null;
  duration: number | null;
  voicemail_seconds: number | null;
  has_voicemail: boolean;
  error: string | null;
  note: string | null;
  handled_at: string | null;
  handled_name: string | null;
  source: 'twilio' | 'manual';
}
interface CallFull extends CallRow {
  earlier: CallRow[];
}
interface Overview {
  ready: boolean;
  last_error: string | null;
  open: boolean | null;
  month: { incoming: number; answered: number; outgoing: number; talk: number; wait: number; today: number; waiting: number };
  team: { available: number; total: number };
  me: { phone: string | null; available: boolean } | null;
  live: CallRow[];
  call_back: CallRow[];
  days: { day: string; answered: number; missed: number; outgoing: number }[];
}
interface Hours {
  open: boolean;
  from: string;
  to: string;
}
interface Settings {
  account_sid: string | null;
  caller_id: string | null;
  site_url: string | null;
  business_name: string | null;
  language: string;
  greeting: string;
  voicemail: boolean;
  voicemail_prompt: string;
  closed_prompt: string;
  hours: Hours[];
  ring_seconds: number;
  verified_at: string | null;
  account_name: string | null;
  last_error: string | null;
  has_token: boolean;
  token_readable: boolean;
  can_keep_token: boolean;
  missing: string[];
  incoming_url: string | null;
  status_url: string | null;
}
interface Agent {
  id: string;
  user: string;
  name: string;
  phone: string | null;
  available: boolean;
  calls: number;
  talk: number;
  busy: boolean;
}

const TABS: { key: Tab; icon: IconName; perm?: string }[] = [
  { key: 'calls', icon: 'phone' },
  { key: 'team', icon: 'users' },
  { key: 'settings', icon: 'settings', perm: 'voip.settings' },
];
const STATE: Record<CState, { tone: Tone; icon: IconName }> = {
  ringing: { tone: 'info', icon: 'bell' },
  in_progress: { tone: 'primary', icon: 'phone' },
  completed: { tone: 'ok', icon: 'check' },
  missed: { tone: 'danger', icon: 'phoneMissed' },
  voicemail: { tone: 'warn', icon: 'voicemail' },
  no_answer: { tone: 'neutral', icon: 'phoneMissed' },
  busy: { tone: 'neutral', icon: 'minus' },
  failed: { tone: 'danger', icon: 'alert' },
  cancelled: { tone: 'neutral', icon: 'x' },
};
const LIVE: CState[] = ['ringing', 'in_progress'];

/** "4:05" for minutes and seconds, "1:02:30" past an hour. */
export function talkTime(s: number | null) {
  if (s === null) return '—';
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = String(s % 60).padStart(2, '0');
  return h ? `${h}:${String(m).padStart(2, '0')}:${sec}` : `${m}:${sec}`;
}
const hoursOf = (s: number) => (s >= 3600 ? t('voip.hours_n', { h: (s / 3600).toFixed(1) }) : t('voip.minutes_n', { m: Math.round(s / 60) }));
const pct = (a: number, b: number) => (b ? Math.round((a / b) * 100) : 0);
const who = (c: Pick<CallRow, 'customer_name' | 'phone'>) => c.customer_name ?? (c.phone.startsWith('+') ? c.phone : t('voip.hidden_number'));

function DirIcon({ c }: { c: Pick<CallRow, 'direction' | 'state'> }) {
  const missed = c.state === 'missed' || c.state === 'voicemail';
  const icon: IconName = c.direction === 'out' ? 'phoneOut' : missed ? 'phoneMissed' : 'phoneIn';
  return (
    <span className={`voip-dir ${c.direction}${missed ? ' missed' : ''}`} title={t(c.direction === 'in' ? 'voip.incoming' : 'voip.outgoing')}>
      <Icon name={icon} size={16} />
    </span>
  );
}
function CallBadge({ state }: { state: CState }) {
  const s = STATE[state];
  return <StatusBadge tone={s.tone} icon={s.icon} label={t(`voip.st_${state}` as MessageKey)} pulse={LIVE.includes(state)} />;
}

export function Calls() {
  const [params, setParams] = useSearchParams();
  const { can } = useSession();
  const tabs = TABS.filter((x) => !x.perm || can(x.perm));
  const tab = (tabs.some((x) => x.key === params.get('tab')) ? params.get('tab') : 'calls') as Tab;
  const { data: o, reload } = useAction<Overview>('voip.overview');
  const [version, setVersion] = useState(0);
  const [dialing, setDialing] = useState<{ customer?: { id: string; name: string; phone: string | null }; from_call?: string } | null>(null);
  const [logging, setLogging] = useState(false);
  const [myPhone, setMyPhone] = useState(false);
  const open = params.get('c');
  const set = (next: Record<string, string | null>) => {
    const p = new URLSearchParams(params);
    for (const [k, v] of Object.entries(next)) v === null ? p.delete(k) : p.set(k, v);
    setParams(p, { replace: true });
  };
  const changed = async () => {
    setVersion((v) => v + 1);
    await reload();
  };
  useEffect(
    () =>
      subscribe(['voip'], () => {
        setVersion((v) => v + 1);
        void reload();
      }),
    [reload],
  );
  const caller = can('voip.call');

  return (
    <Shell>
      <div className="page reports voip-page">
        <div className="page-head">
          <div>
            <h1>{t('voip.title')}</h1>
            <p className="muted">{t('voip.subtitle')}</p>
          </div>
          <div className="page-actions">
            {caller && o ? (
              <button className={`voip-me${o.me?.available ? ' on' : ''}`} onClick={() => setMyPhone(true)} title={t('voip.my_phone')}>
                <span className="voip-me-dot" aria-hidden="true" />
                <span className="vat-party">
                  <strong>{o.me?.available ? t('voip.available') : t('voip.away')}</strong>
                  <span className="muted small tabular">{o.me?.phone ?? t('voip.no_phone_yet')}</span>
                </span>
              </button>
            ) : null}
            {caller ? (
              <Button icon="note" onClick={() => setLogging(true)}>
                {t('voip.log_call')}
              </Button>
            ) : null}
            {caller ? (
              <Button variant="primary" icon="phoneOut" onClick={() => setDialing({})}>
                {t('voip.call')}
              </Button>
            ) : null}
          </div>
        </div>
        {o && !o.ready ? (
          <p className="callout callout-warn">
            <Icon name="alert" size={16} />
            <span>
              {t('voip.not_ready')}{' '}
              {can('voip.settings') ? (
                <button className="link" onClick={() => set({ tab: 'settings', c: null })}>
                  {t('voip.set_up')}
                </button>
              ) : (
                t('voip.ask_manager')
              )}
            </span>
          </p>
        ) : o?.last_error ? (
          <p className="callout callout-warn">
            <Icon name="alert" size={16} />
            <span>{t('voip.twilio_error', { error: o.last_error })}</span>
          </p>
        ) : null}
        {!o ? (
          <Skeleton h={110} r="var(--radius-lg)" />
        ) : (
          <section className="rep-kpis" aria-label={t('voip.at_a_glance')}>
            <Kpi label={t('voip.k_call_back')} value={String(o.month.waiting)} hint={o.month.waiting ? t('voip.k_call_back_hint') : t('voip.k_nobody_waiting')} warn={o.month.waiting > 0} />
            <Kpi
              label={t('voip.k_answered')}
              value={o.month.incoming ? `${pct(o.month.answered, o.month.incoming)}%` : '—'}
              hint={o.month.incoming ? t('voip.k_answered_hint', { a: o.month.answered, n: o.month.incoming }) : t('voip.k_no_incoming')}
              warn={o.month.incoming > 0 && pct(o.month.answered, o.month.incoming) < 80}
            />
            <Kpi label={t('voip.k_wait')} value={o.month.wait ? t('voip.seconds_n', { s: o.month.wait }) : '—'} hint={t('voip.k_wait_hint')} />
            <Kpi label={t('voip.k_outgoing')} value={String(o.month.outgoing)} hint={t('voip.k_talk', { time: hoursOf(o.month.talk) })} />
          </section>
        )}
        {o?.live.length ? <LiveCalls calls={o.live} onOpen={(id) => set({ c: id })} onChange={changed} /> : null}
        <div className="segmented tabs voip-tabs" role="tablist" aria-label={t('voip.sections')}>
          {tabs.map((x) => (
            <button key={x.key} role="tab" aria-selected={tab === x.key} className={tab === x.key ? 'active' : ''} onClick={() => set({ tab: x.key === 'calls' ? null : x.key, f: null })}>
              <Icon name={x.icon} size={16} />
              {t(`voip.tab_${x.key}` as MessageKey)}
              {x.key === 'team' && o ? <span className="count">{`${o.team.available}/${o.team.total}`}</span> : null}
            </button>
          ))}
        </div>
        {!o ? null : tab === 'calls' ? (
          <CallsTab key={version} o={o} params={params} set={set} onCall={caller ? (c) => setDialing(c) : undefined} />
        ) : tab === 'team' ? (
          <TeamTab key={version} onChange={changed} onMine={() => setMyPhone(true)} />
        ) : (
          <SettingsTab onChange={changed} />
        )}
      </div>
      {open ? (
        <CallDialog key={`${open}-${version}`} id={open} onClose={() => set({ c: null })} onChange={changed} onCall={caller ? (c) => setDialing(c) : undefined} onOpen={(id) => set({ c: id })} />
      ) : null}
      {dialing ? (
        <DialDialog
          initial={dialing}
          me={o?.me ?? null}
          ready={o?.ready ?? false}
          onClose={() => setDialing(null)}
          onMyPhone={() => setMyPhone(true)}
          onStarted={async () => {
            setDialing(null);
            await changed();
          }}
        />
      ) : null}
      {logging ? (
        <LogDialog
          onClose={() => setLogging(false)}
          onSaved={async (id) => {
            setLogging(false);
            await changed();
            set({ c: id });
          }}
        />
      ) : null}
      {myPhone ? (
        <MyPhoneDialog
          onClose={() => setMyPhone(false)}
          onSaved={async () => {
            setMyPhone(false);
            await changed();
          }}
        />
      ) : null}
    </Shell>
  );
}

// ---- live calls --------------------------------------------------------------------------------------

function LiveCalls({ calls, onOpen, onChange }: { calls: CallRow[]; onOpen: (id: string) => void; onChange: () => Promise<void> }) {
  const { me } = useSession();
  const { busy, run } = useSave();
  const [, tick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => tick((n) => n + 1), 1000);
    return () => clearInterval(id);
  }, []);
  return (
    <section className="voip-live" aria-label={t('voip.live')}>
      {calls.map((c) => {
        const mine = c.agent === me?.userId;
        const from = c.answered_at ?? c.started_at;
        const secs = Math.max(0, Math.round((Date.now() - new Date(from).getTime()) / 1000));
        return (
          <article key={c.id} className={`voip-live-card ${c.state}`}>
            <span className="voip-live-icon" aria-hidden="true">
              <Icon name={c.direction === 'in' ? 'phoneIn' : 'phoneOut'} size={20} />
            </span>
            <button className="voip-live-who" onClick={() => onOpen(c.id)}>
              <strong>{who(c)}</strong>
              <span className="muted small">
                {c.state === 'ringing'
                  ? c.direction === 'in'
                    ? t('voip.live_ringing_in')
                    : t('voip.live_ringing_out', { name: c.agent_name ?? '' })
                  : t('voip.live_talking', { name: c.agent_name ?? t('voip.someone') })}
              </span>
            </button>
            <span className="voip-live-time tabular" aria-label={t('voip.duration')}>
              {talkTime(secs)}
            </span>
            {mine || c.direction === 'in' ? null : <span className="muted small">{c.agent_name}</span>}
            {mine ? (
              <Button variant="danger" icon="phoneMissed" busy={busy} onClick={() => void run(async () => (await action('voip.hangup', { id: c.id }), await onChange()), t('voip.ended_toast'))}>
                {t('voip.hang_up')}
              </Button>
            ) : null}
          </article>
        );
      })}
    </section>
  );
}

// ---- calls ----------------------------------------------------------------------------------------------

/** Calls shown at first; more on request. */
const PAGE = 40;

type DialStart = { customer?: { id: string; name: string; phone: string | null }; from_call?: string };

function CallsTab({ o, params, set, onCall }: { o: Overview; params: URLSearchParams; set: (n: Record<string, string | null>) => void; onCall?: (c: DialStart) => void }) {
  const filter = (FILTERS as readonly string[]).includes(params.get('f') ?? '') ? (params.get('f') as Filter) : 'all';
  const [q, setQ] = useState('');
  const [query, setQuery] = useState('');
  const [shown, setShown] = useState(PAGE);
  useEffect(() => {
    const id = setTimeout(() => setQuery(q.trim()), 250);
    return () => clearTimeout(id);
  }, [q]);
  const { data } = useAction<CallRow[]>('voip.calls', { filter, q: query });
  const max = Math.max(1, ...o.days.map((d) => d.answered + d.missed + d.outgoing));
  const fmt = new Intl.DateTimeFormat(undefined, { weekday: 'short', day: 'numeric' });
  return (
    <div className="form-stack">
      {filter === 'all' && !query && o.call_back.length ? (
        <section className="panel voip-back" aria-label={t('voip.to_call_back')}>
          <div className="voip-back-head">
            <h2 className="rep-sub">
              <Icon name="phoneMissed" size={18} /> {t('voip.to_call_back')}
            </h2>
            {o.month.waiting > o.call_back.length ? (
              <button className="link" onClick={() => set({ f: 'call_back' })}>
                {tn('voip.see_all_n', o.month.waiting)}
              </button>
            ) : null}
          </div>
          <ul className="voip-back-list">
            {o.call_back.map((c) => (
              <li key={c.id}>
                <button className="voip-back-who" onClick={() => set({ c: c.id })}>
                  <DirIcon c={c} />
                  <span className="vat-party">
                    <strong>{who(c)}</strong>
                    <span className="muted small">
                      {c.state === 'voicemail' ? t('voip.left_voicemail', { s: c.voicemail_seconds ?? 0 }) : t('voip.st_missed')} · {since(c.started_at)}
                    </span>
                  </span>
                </button>
                {onCall ? (
                  <Button icon="phoneOut" onClick={() => onCall({ from_call: c.id, customer: c.customer ? { id: c.customer, name: c.customer_name ?? c.phone, phone: c.phone } : undefined })}>
                    {t('voip.call_back')}
                  </Button>
                ) : null}
              </li>
            ))}
          </ul>
        </section>
      ) : null}
      <section className="panel voip-chart" aria-label={t('voip.two_weeks')}>
        <div className="voip-back-head">
          <h2 className="rep-sub">{t('voip.two_weeks')}</h2>
          <span className="voip-legend muted small">
            <i className="answered" /> {t('voip.l_answered')} <i className="missed" /> {t('voip.l_missed')} <i className="outgoing" /> {t('voip.l_outgoing')}
          </span>
        </div>
        <div className="voip-bars" role="list">
          {o.days.map((d) => {
            const label = fmt.format(new Date(`${d.day}T12:00:00`));
            const tip = t('voip.day_tip', { day: label, a: d.answered, m: d.missed, o: d.outgoing });
            return (
              <div key={d.day} className="voip-bar" role="listitem" title={tip} aria-label={tip}>
                <span className="voip-bar-stack">
                  <span className="outgoing" style={{ height: `${(d.outgoing / max) * 100}%` }} />
                  <span className="missed" style={{ height: `${(d.missed / max) * 100}%` }} />
                  <span className="answered" style={{ height: `${(d.answered / max) * 100}%` }} />
                </span>
                <span className="voip-bar-label muted small">{label}</span>
              </div>
            );
          })}
        </div>
      </section>
      <div className="voip-toolbar">
        <div className="segmented" role="group" aria-label={t('voip.show')}>
          {FILTERS.map((f) => (
            <button key={f} className={filter === f ? 'active' : ''} aria-pressed={filter === f} onClick={() => set({ f: f === 'all' ? null : f })}>
              {t(`voip.f_${f}` as MessageKey)}
              {f === 'call_back' && o.month.waiting ? <span className="count">{o.month.waiting}</span> : null}
            </button>
          ))}
        </div>
        <label className="search">
          <Icon name="search" size={16} />
          <input className="input" type="search" value={q} placeholder={t('voip.search')} aria-label={t('voip.search')} onChange={(e) => setQ(e.target.value)} />
        </label>
      </div>
      {!data ? (
        <Skeleton h={300} />
      ) : !data.length ? (
        <EmptyState icon="phone" title={query ? t('voip.nothing_found') : t(`voip.empty_${filter}` as MessageKey)} body={filter === 'all' && !query ? t('voip.empty_all_hint') : undefined} />
      ) : (
        <Table
          head={[t('voip.c_call'), t('voip.c_who'), t('voip.c_by'), t('voip.c_when'), t('voip.c_length')]}
          phoneHide={[2, 4]}
          left={[0, 1, 2]}
          rows={data.slice(0, shown).map((c) => ({
            key: c.id,
            onClick: () => set({ c: c.id }),
            cells: [
              <span className="voip-cell-state" key="s">
                <DirIcon c={c} />
                <CallBadge state={c.state} />
                {c.source === 'manual' ? <span className="muted small">{t('voip.by_hand')}</span> : null}
              </span>,
              <span className="vat-party" key="w">
                <strong>{who(c)}</strong>
                <span className="muted small">{c.note ?? (c.customer_name ? c.phone : '')}</span>
              </span>,
              c.agent_name ?? <span className="muted">—</span>,
              <span className="tabular" key="d" title={dateTime(c.started_at)}>
                {dateTime(c.started_at)}
              </span>,
              <span className="tabular" key="l">
                {c.state === 'voicemail' ? (
                  <span className="voip-vm-len">
                    <Icon name="voicemail" size={14} /> {talkTime(c.voicemail_seconds)}
                  </span>
                ) : (
                  talkTime(c.duration)
                )}
              </span>,
            ],
          }))}
        />
      )}
      {data && data.length > shown ? (
        <div className="row-actions voip-more">
          <Button icon="down" onClick={() => setShown((n) => n + PAGE)}>
            {t('voip.show_more', { n: Math.min(PAGE, data.length - shown) })}
          </Button>
        </div>
      ) : null}
    </div>
  );
}

function CallDialog({ id, onClose, onChange, onCall, onOpen }: { id: string; onClose: () => void; onChange: () => Promise<void>; onCall?: (c: DialStart) => void; onOpen: (id: string) => void }) {
  const { can } = useSession();
  const { busy, run } = useSave();
  const { fail } = useUi();
  const { data: c, reload } = useAction<CallFull>('voip.call', { id });
  const [note, setNote] = useState<string | null>(null);
  const [audio, setAudio] = useState<string | null>(null);
  const [loadingAudio, setLoadingAudio] = useState(false);
  const [picking, setPicking] = useState(false);
  const caller = can('voip.call');
  if (!c) {
    return (
      <Dialog title={t('voip.call_title')} onClose={onClose} size="md">
        <Skeleton h={260} />
      </Dialog>
    );
  }
  const waiting = (c.state === 'missed' || c.state === 'voicemail') && !c.handled_at;
  const done = async () => {
    await reload();
    await onChange();
  };
  const play = async () => {
    setLoadingAudio(true);
    try {
      const r = await action<{ mime: string; data: string }>('voip.voicemail', { id: c.id });
      setAudio(`data:${r.mime};base64,${r.data}`);
    } catch (err) {
      fail(err);
    } finally {
      setLoadingAudio(false);
    }
  };
  return (
    <>
      <Dialog
        title={who(c)}
        onClose={onClose}
        size="md"
        footer={
          <div className="row-actions">
            {caller && waiting ? (
              <Button icon="check" busy={busy} onClick={() => void run(async () => (await action('voip.handled', { id: c.id, done: true }), await done()), t('voip.handled_toast'))}>
                {t('voip.mark_handled')}
              </Button>
            ) : null}
            {caller && c.handled_at && (c.state === 'missed' || c.state === 'voicemail') ? (
              <Button icon="undo" busy={busy} onClick={() => void run(async () => (await action('voip.handled', { id: c.id, done: false }), await done()))}>
                {t('voip.not_handled')}
              </Button>
            ) : null}
            {onCall && c.phone.startsWith('+') ? (
              <Button
                variant="primary"
                icon="phoneOut"
                onClick={() => onCall({ from_call: c.id, customer: c.customer ? { id: c.customer, name: c.customer_name ?? c.phone, phone: c.phone } : undefined })}
              >
                {waiting ? t('voip.call_back') : t('voip.call')}
              </Button>
            ) : null}
          </div>
        }
      >
        <div className="form-stack">
          <div className="voip-head">
            <DirIcon c={c} />
            <CallBadge state={c.state} />
            <span className="muted small">{c.source === 'manual' ? t('voip.written_by_hand') : t(c.direction === 'in' ? 'voip.incoming' : 'voip.outgoing')}</span>
          </div>
          {c.error ? (
            <p className="callout callout-warn">
              <Icon name="alert" size={16} />
              <span>{c.error}</span>
            </p>
          ) : null}
          <dl className="lc-facts voip-kv">
            <dt>{t('voip.number')}</dt>
            <dd className="tabular">{c.phone.startsWith('+') ? <a href={`tel:${c.phone}`}>{c.phone}</a> : t('voip.hidden_number')}</dd>
            <dt>{t('voip.customer')}</dt>
            <dd>
              {c.customer_name ?? <span className="muted">{t('voip.no_customer')}</span>}{' '}
              {caller && c.phone.startsWith('+') ? (
                <button className="link" onClick={() => setPicking(true)}>
                  {c.customer ? t('voip.change') : t('voip.link_customer')}
                </button>
              ) : null}
            </dd>
            <dt>{t('voip.when')}</dt>
            <dd>{dateTime(c.started_at)}</dd>
            {c.agent_name ? (
              <>
                <dt>{c.direction === 'in' ? t('voip.answered_by') : t('voip.made_by')}</dt>
                <dd>{c.agent_name}</dd>
              </>
            ) : null}
            {c.answered_at && c.source === 'twilio' && c.direction === 'in' ? (
              <>
                <dt>{t('voip.waited')}</dt>
                <dd>{t('voip.seconds_n', { s: Math.max(0, Math.round((new Date(c.answered_at).getTime() - new Date(c.started_at).getTime()) / 1000)) })}</dd>
              </>
            ) : null}
            <dt>{t('voip.duration')}</dt>
            <dd className="tabular">{LIVE.includes(c.state) ? t('voip.still_on') : talkTime(c.duration)}</dd>
            {c.handled_at ? (
              <>
                <dt>{t('voip.dealt_with')}</dt>
                <dd>{t('voip.dealt_with_by', { name: c.handled_name ?? '', at: dateTime(c.handled_at) })}</dd>
              </>
            ) : null}
          </dl>
          {c.has_voicemail ? (
            <div className="voip-vm">
              <span className="voip-vm-icon" aria-hidden="true">
                <Icon name="voicemail" size={20} />
              </span>
              <span className="vat-party">
                <strong>{t('voip.voicemail')}</strong>
                <span className="muted small">{t('voip.voicemail_len', { time: talkTime(c.voicemail_seconds) })}</span>
              </span>
              {audio ? (
                // eslint-disable-next-line jsx-a11y/media-has-caption
                <audio controls autoPlay src={audio} className="voip-audio" />
              ) : (
                <Button icon="play" busy={loadingAudio} onClick={() => void play()}>
                  {t('voip.listen')}
                </Button>
              )}
            </div>
          ) : null}
          <Field label={t('voip.note')} hint={t('voip.note_hint')}>
            <textarea className="input textarea" rows={3} maxLength={2000} disabled={!caller} value={note ?? c.note ?? ''} onChange={(e) => setNote(e.target.value)} />
          </Field>
          {note !== null && note !== (c.note ?? '') ? (
            <div className="row-actions">
              <Button size="sm" icon="check" busy={busy} onClick={() => void run(async () => (await action('voip.note', { id: c.id, note }), setNote(null), await done()), t('voip.note_saved'))}>
                {t('voip.save_note')}
              </Button>
            </div>
          ) : null}
          {c.earlier.length ? (
            <section>
              <h3 className="rep-sub">{t('voip.earlier')}</h3>
              <ul className="voip-earlier">
                {c.earlier.map((e) => (
                  <li key={e.id}>
                    <button onClick={() => onOpen(e.id)}>
                      <DirIcon c={e} />
                      <span className="vat-party">
                        <span>{t(`voip.st_${e.state}` as MessageKey)}</span>
                        <span className="muted small">{e.note ?? e.agent_name ?? ''}</span>
                      </span>
                      <span className="muted small tabular">{dateTime(e.started_at)}</span>
                    </button>
                  </li>
                ))}
              </ul>
            </section>
          ) : null}
        </div>
      </Dialog>
      {picking ? (
        <CustomerPicker
          title={t('voip.link_customer')}
          onClose={() => setPicking(false)}
          onPick={async (x) => {
            await action('voip.link_customer', { id: c.id, customer: x.id });
            setPicking(false);
            await done();
          }}
        />
      ) : null}
    </>
  );
}

// ---- making a call -----------------------------------------------------------------------------------

function DialDialog({
  initial,
  me,
  ready,
  onClose,
  onMyPhone,
  onStarted,
}: {
  initial: DialStart;
  me: Overview['me'];
  ready: boolean;
  onClose: () => void;
  onMyPhone: () => void;
  onStarted: () => Promise<void>;
}) {
  const { busy, run } = useSave();
  const [customer, setCustomer] = useState(initial.customer ?? null);
  const [phone, setPhone] = useState('');
  const [picking, setPicking] = useState(false);
  const digits = phone.replace(/\D/g, '');
  const to = customer ? !!customer.phone : !!initial.from_call || digits.length >= 6;
  const press = (k: string) => setPhone((p) => (k === 'back' ? p.slice(0, -1) : `${p}${k}`));
  return (
    <>
      <Dialog
        title={initial.from_call ? t('voip.call_back') : t('voip.call')}
        onClose={onClose}
        size="sm"
        footer={
          <Button
            variant="primary"
            icon="phoneOut"
            busy={busy}
            disabled={!to || !ready || !me?.phone}
            onClick={() =>
              void run(async () => {
                await action(
                  'voip.dial',
                  customer
                    ? { customer: customer.id, ...(initial.from_call ? { from_call: initial.from_call } : {}) }
                    : initial.from_call && !digits
                      ? { from_call: initial.from_call }
                      : { phone: phone.trim() },
                );
                await onStarted();
              }, t('voip.ringing_you'))
            }
          >
            {t('voip.call_now')}
          </Button>
        }
      >
        <div className="form-stack">
          {!ready ? (
            <p className="callout callout-warn">
              <Icon name="alert" size={16} />
              <span>{t('voip.not_ready_short')}</span>
            </p>
          ) : !me?.phone ? (
            <p className="callout callout-warn">
              <Icon name="alert" size={16} />
              <span>
                {t('voip.need_my_phone')}{' '}
                <button className="link" onClick={onMyPhone}>
                  {t('voip.write_my_phone')}
                </button>
              </span>
            </p>
          ) : (
            <p className="callout callout-info voip-how">
              <Icon name="info" size={16} />
              <span>{t('voip.how_it_works', { phone: me.phone })}</span>
            </p>
          )}
          {customer ? (
            <div className="voip-to">
              <span className="voip-avatar" aria-hidden="true">
                <Icon name="contact" size={18} />
              </span>
              <span className="vat-party">
                <strong>{customer.name}</strong>
                <span className="muted tabular">{customer.phone ?? t('voip.no_phone')}</span>
              </span>
              <IconButton icon="x" label={t('voip.remove')} onClick={() => setCustomer(null)} />
            </div>
          ) : (
            <>
              <Field label={t('voip.number')} hint={initial.from_call && !digits ? t('voip.number_from_call') : undefined}>
                <span className="sms-to">
                  <input className="input voip-number tabular" type="tel" inputMode="tel" autoFocus value={phone} placeholder="+355 69 …" onChange={(e) => setPhone(e.target.value)} />
                  <Button icon="contact" onClick={() => setPicking(true)}>
                    {t('voip.pick_customer')}
                  </Button>
                </span>
              </Field>
              <div className="voip-pad" role="group" aria-label={t('voip.keypad')}>
                {['1', '2', '3', '4', '5', '6', '7', '8', '9', '+', '0', 'back'].map((k) => (
                  <button key={k} type="button" onClick={() => press(k)} aria-label={k === 'back' ? t('voip.delete_digit') : k}>
                    {k === 'back' ? <Icon name="back" size={18} /> : k}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      </Dialog>
      {picking ? (
        <CustomerPicker
          title={t('voip.pick_customer')}
          onClose={() => setPicking(false)}
          onPick={(x) => {
            setCustomer({ id: x.id, name: x.name, phone: x.phone });
            setPicking(false);
          }}
        />
      ) : null}
    </>
  );
}

const OUTCOMES = ['completed', 'no_answer', 'busy', 'missed'] as const;
const localNow = () => {
  const d = new Date(Date.now() - new Date().getTimezoneOffset() * 60_000);
  return d.toISOString().slice(0, 16);
};

function LogDialog({ onClose, onSaved }: { onClose: () => void; onSaved: (id: string) => Promise<void> }) {
  const { busy, run } = useSave();
  const [direction, setDirection] = useState<Dir>('out');
  const [customer, setCustomer] = useState<{ id: string; name: string; phone: string | null } | null>(null);
  const [phone, setPhone] = useState('');
  const [outcome, setOutcome] = useState<(typeof OUTCOMES)[number]>('completed');
  const [at, setAt] = useState(localNow);
  const [minutes, setMinutes] = useState('');
  const [note, setNote] = useState('');
  const [picking, setPicking] = useState(false);
  const outcomes = OUTCOMES.filter((x) => direction === 'in' || x !== 'missed');
  const ok = (customer ? !!customer.phone : phone.replace(/\D/g, '').length >= 6) && !!at;
  return (
    <>
      <Dialog
        title={t('voip.log_call')}
        onClose={onClose}
        size="md"
        footer={
          <Button
            variant="primary"
            icon="check"
            busy={busy}
            disabled={!ok}
            onClick={() =>
              void run(async () => {
                const r = await action<{ id: string }>('voip.log', {
                  direction,
                  ...(customer ? { customer: customer.id } : { phone: phone.trim() }),
                  outcome,
                  at: new Date(at).toISOString(),
                  minutes: outcome === 'completed' && minutes ? Number(minutes.replace(',', '.')) : null,
                  note: note.trim() || null,
                });
                await onSaved(r.id);
              }, t('voip.logged_toast'))
            }
          >
            {t('voip.save_call')}
          </Button>
        }
      >
        <div className="form-stack">
          <p className="muted small">{t('voip.log_hint')}</p>
          <div className="segmented" role="group" aria-label={t('voip.direction')}>
            {(['out', 'in'] as const).map((d) => (
              <button
                key={d}
                className={direction === d ? 'active' : ''}
                aria-pressed={direction === d}
                onClick={() => {
                  setDirection(d);
                  if (d === 'out' && outcome === 'missed') setOutcome('no_answer');
                }}
              >
                <Icon name={d === 'in' ? 'phoneIn' : 'phoneOut'} size={16} />
                {t(d === 'in' ? 'voip.they_called' : 'voip.i_called')}
              </button>
            ))}
          </div>
          <Field label={t('voip.who')}>
            {customer ? (
              <span className="sms-to">
                <span className="vat-party">
                  <strong>{customer.name}</strong>
                  <span className="muted tabular">{customer.phone ?? t('voip.no_phone')}</span>
                </span>
                <IconButton icon="x" label={t('voip.remove')} onClick={() => setCustomer(null)} />
              </span>
            ) : (
              <span className="sms-to">
                <input className="input" type="tel" inputMode="tel" value={phone} placeholder="+355 69 …" onChange={(e) => setPhone(e.target.value)} />
                <Button icon="contact" onClick={() => setPicking(true)}>
                  {t('voip.pick_customer')}
                </Button>
              </span>
            )}
          </Field>
          <Field label={t('voip.outcome')}>
            <div className="segmented voip-wrap" role="group" aria-label={t('voip.outcome')}>
              {outcomes.map((x) => (
                <button key={x} className={outcome === x ? 'active' : ''} aria-pressed={outcome === x} onClick={() => setOutcome(x)}>
                  {t(`voip.o_${x}` as MessageKey)}
                </button>
              ))}
            </div>
          </Field>
          <div className="form-grid">
            <Field label={t('voip.when')}>
              <input className="input" type="datetime-local" value={at} max={localNow()} onChange={(e) => setAt(e.target.value)} />
            </Field>
            {outcome === 'completed' ? (
              <Field label={t('voip.length')}>
                <span className="input-unit">
                  <input className="input" inputMode="decimal" value={minutes} placeholder="5" onChange={(e) => setMinutes(e.target.value.replace(/[^\d.,]/g, '').slice(0, 5))} />
                  <span>{t('voip.min')}</span>
                </span>
              </Field>
            ) : null}
          </div>
          <Field label={t('voip.note')}>
            <textarea className="input textarea" rows={3} maxLength={2000} value={note} placeholder={t('voip.note_placeholder')} onChange={(e) => setNote(e.target.value)} />
          </Field>
        </div>
      </Dialog>
      {picking ? (
        <CustomerPicker
          title={t('voip.pick_customer')}
          onClose={() => setPicking(false)}
          onPick={(x) => {
            setCustomer({ id: x.id, name: x.name, phone: x.phone });
            setPicking(false);
          }}
        />
      ) : null}
    </>
  );
}

function MyPhoneDialog({ onClose, onSaved }: { onClose: () => void; onSaved: () => Promise<void> }) {
  const { busy, run } = useSave();
  const { data } = useAction<{ phone: string | null; available: boolean }>('voip.me');
  const [phone, setPhone] = useState<string | null>(null);
  const [available, setAvailable] = useState<boolean | null>(null);
  const p = phone ?? data?.phone ?? '';
  const a = available ?? data?.available ?? false;
  return (
    <Dialog
      title={t('voip.my_phone')}
      onClose={onClose}
      size="sm"
      footer={
        <Button
          variant="primary"
          icon="check"
          busy={busy}
          disabled={!data}
          onClick={() => void run(async () => (await action('voip.set_me', { phone: p.trim() || null, available: a }), await onSaved()), t('voip.saved_toast'))}
        >
          {t('common.save')}
        </Button>
      }
    >
      {!data ? (
        <Skeleton h={160} />
      ) : (
        <div className="form-stack">
          <Field label={t('voip.my_number')} hint={t('voip.my_number_hint')}>
            <input className="input tabular" type="tel" inputMode="tel" autoFocus value={p} placeholder="+355 69 …" onChange={(e) => setPhone(e.target.value)} />
          </Field>
          <label className="setting-row sms-row">
            <span>
              <strong>{t('voip.take_calls')}</strong>
              <span className="muted small">{t('voip.take_calls_hint')}</span>
            </span>
            <Switch checked={a} onChange={setAvailable} label={t('voip.take_calls')} disabled={!p.trim()} />
          </label>
        </div>
      )}
    </Dialog>
  );
}

// ---- team -------------------------------------------------------------------------------------------

function TeamTab({ onChange, onMine }: { onChange: () => Promise<void>; onMine: () => void }) {
  const { can, me } = useSession();
  const { fail } = useUi();
  const { data, reload } = useAction<Agent[]>('voip.agents');
  const manage = can('voip.settings');
  if (!data) return <Skeleton h={240} />;
  const toggle = async (a: Agent, on: boolean) => {
    try {
      await action('voip.set_agent', { id: a.id, available: on });
      await reload();
      await onChange();
    } catch (err) {
      fail(err);
    }
  };
  return (
    <div className="form-stack">
      <p className="muted">{t('voip.team_hint')}</p>
      {!data.length ? (
        <EmptyState icon="users" title={t('voip.team_empty')} body={t('voip.team_empty_hint')} action={can('voip.call') ? <Button onClick={onMine}>{t('voip.write_my_phone')}</Button> : undefined} />
      ) : (
        <Table
          head={[t('voip.c_person'), t('voip.c_phone'), t('voip.c_calls'), t('voip.c_talk'), t('voip.c_takes')]}
          phoneHide={[1, 3]}
          left={[0, 1]}
          rows={data.map((a) => ({
            key: a.id,
            cells: [
              <span className="voip-person" key="p">
                <strong>{a.name}</strong>
                {a.busy ? <StatusBadge tone="primary" icon="phone" label={t('voip.on_a_call')} pulse /> : null}
              </span>,
              <span className="tabular" key="n">
                {a.phone ?? <span className="muted">—</span>}
              </span>,
              String(a.calls),
              hoursOf(a.talk),
              a.user === me?.userId ? (
                <button className="link" key="t" onClick={onMine}>
                  {a.available ? t('voip.available') : t('voip.away')}
                </button>
              ) : manage ? (
                <Switch key="t" checked={a.available} onChange={(on) => void toggle(a, on)} label={t('voip.takes_calls_of', { name: a.name })} disabled={!a.phone} />
              ) : (
                <span key="t" className={a.available ? '' : 'muted'}>
                  {a.available ? t('voip.available') : t('voip.away')}
                </span>
              ),
            ],
          }))}
        />
      )}
    </div>
  );
}

// ---- settings ---------------------------------------------------------------------------------------

function SettingsTab({ onChange }: { onChange: () => Promise<void> }) {
  const { busy, run } = useSave();
  const { toast, fail } = useUi();
  const { data, reload } = useAction<Settings>('voip.settings');
  const [f, setF] = useState<Settings | null>(null);
  const [token, setToken] = useState('');
  const [checking, setChecking] = useState(false);
  const [tried, setTried] = useState(false);
  const loaded = useRef(false);
  useEffect(() => {
    if (!data || loaded.current) return;
    loaded.current = true;
    setF({ ...data, site_url: data.site_url ?? (window.location.protocol === 'https:' ? window.location.origin : null) });
  }, [data]);
  if (!f || !data) return <Skeleton h={400} />;
  const set = <K extends keyof Settings>(k: K, v: Settings[K]) => setF({ ...f, [k]: v });
  const setDay = (i: number, h: Partial<Hours>) =>
    set(
      'hours',
      f.hours.map((x, j) => (j === i ? { ...x, ...h } : x)),
    );
  const errors = {
    account_sid: f.account_sid && !/^AC[0-9a-fA-F]{32}$/.test(f.account_sid) ? t('voip.e_account') : null,
    token: token && !/^[0-9a-fA-F]{32}$/.test(token) ? t('voip.e_token') : null,
    site_url: f.site_url && !/^https:\/\/[a-z0-9.-]+(:\d+)?(\/[\w./-]*)?$/i.test(f.site_url) ? t('voip.e_site') : null,
    hours: f.hours.some((h) => h.open && h.from === h.to) ? t('voip.e_hours') : null,
  };
  const save = () => {
    setTried(true);
    if (Object.values(errors).some(Boolean)) return;
    void run(async () => {
      const out = await action<Settings>('voip.save_settings', {
        account_sid: f.account_sid || null,
        ...(token ? { token } : {}),
        caller_id: f.caller_id || null,
        site_url: f.site_url || null,
        business_name: f.business_name?.trim() || null,
        language: f.language,
        greeting: f.greeting,
        voicemail: f.voicemail,
        voicemail_prompt: f.voicemail_prompt,
        closed_prompt: f.closed_prompt,
        hours: f.hours,
        ring_seconds: f.ring_seconds,
      });
      setToken('');
      setF(out);
      await reload();
      await onChange();
    }, t('voip.settings_saved'));
  };
  const verify = async () => {
    setChecking(true);
    try {
      const r = await action<{ ok: boolean; name?: string; error?: string }>('voip.verify', {});
      if (r.ok) toast(t('voip.verified_toast', { name: r.name ?? '' }));
      else fail(new Error(r.error ?? ''));
      await reload();
      await onChange();
    } catch (err) {
      fail(err);
    } finally {
      setChecking(false);
    }
  };
  const err = (e: string | null) => (tried ? e : null);
  const copy = (text: string) => void navigator.clipboard?.writeText(text).then(() => toast(t('voip.copied')), fail);
  return (
    <div className="form-stack">
      {data.verified_at ? (
        <p className="callout callout-info">
          <Icon name="checkCircle" size={16} />
          <span>{t('voip.verified', { name: data.account_name ?? '', at: dateTime(data.verified_at) })}</span>
        </p>
      ) : data.last_error ? (
        <p className="callout callout-warn">
          <Icon name="alert" size={16} />
          <span>{t('voip.twilio_error', { error: data.last_error })}</span>
        </p>
      ) : null}
      {!data.can_keep_token ? (
        <p className="callout callout-warn">
          <Icon name="alert" size={16} />
          <span>{t('voip.no_key')}</span>
        </p>
      ) : data.has_token && !data.token_readable ? (
        <p className="callout callout-warn">
          <Icon name="alert" size={16} />
          <span>{t('voip.token_lost')}</span>
        </p>
      ) : null}
      <div className={`sms-ready-card${data.missing.length ? '' : ' ok'}`}>
        <Icon name={data.missing.length ? 'circle' : 'checkCircle'} size={18} />
        <span className="vat-party">
          <strong>{t('voip.calls_status')}</strong>
          <span className="muted small">{data.missing.length ? t('voip.missing', { what: data.missing.map((m) => t(`voip.miss_${m}` as MessageKey)).join(', ') }) : t('voip.ready')}</span>
        </span>
      </div>
      <section className="panel form-stack sub-form">
        <div>
          <h2 className="rep-sub">{t('voip.account')}</h2>
          <p className="muted small">{t('voip.account_hint')}</p>
        </div>
        <div className="form-grid">
          <Field label={t('voip.account_sid')} error={err(errors.account_sid)} hint={t('voip.account_sid_hint')}>
            <input className="input tabular" value={f.account_sid ?? ''} spellCheck={false} autoComplete="off" placeholder="AC…" onChange={(e) => set('account_sid', e.target.value.trim() || null)} />
          </Field>
          <Field label={t('voip.token')} error={err(errors.token)} hint={data.has_token && !token ? t('voip.token_kept') : t('voip.token_hint')}>
            <input
              className="input"
              type="password"
              autoComplete="new-password"
              value={token}
              placeholder={data.has_token ? '••••••••' : ''}
              disabled={!data.can_keep_token}
              onChange={(e) => setToken(e.target.value.trim())}
            />
          </Field>
        </div>
        <div className="form-grid">
          <Field label={t('voip.caller_id')} hint={t('voip.caller_id_hint')}>
            <input className="input tabular" type="tel" value={f.caller_id ?? ''} maxLength={40} placeholder="+355 4 …" onChange={(e) => set('caller_id', e.target.value || null)} />
          </Field>
          <Field label={t('voip.business_name')} hint={t('voip.business_name_hint')}>
            <input className="input" value={f.business_name ?? ''} maxLength={60} onChange={(e) => set('business_name', e.target.value || null)} />
          </Field>
        </div>
      </section>
      <section className="panel form-stack sub-form">
        <div>
          <h2 className="rep-sub">{t('voip.connect')}</h2>
          <p className="muted small">{t('voip.connect_hint')}</p>
        </div>
        <Field label={t('voip.site_url')} error={err(errors.site_url)} hint={t('voip.site_url_hint')}>
          <input className="input" value={f.site_url ?? ''} spellCheck={false} placeholder="https://" onChange={(e) => set('site_url', e.target.value.trim().replace(/\/+$/, '') || null)} />
        </Field>
        {data.incoming_url && data.status_url ? (
          <div className="lc-link-box">
            <p className="muted small">{t('voip.incoming_hint')}</p>
            <div className="lc-link">
              <code>{data.incoming_url}</code>
              <IconButton icon="copy" label={t('voip.copy')} onClick={() => copy(data.incoming_url!)} />
            </div>
            <p className="muted small">{t('voip.status_hint')}</p>
            <div className="lc-link">
              <code>{data.status_url}</code>
              <IconButton icon="copy" label={t('voip.copy')} onClick={() => copy(data.status_url!)} />
            </div>
          </div>
        ) : (
          <p className="muted small">{t('voip.links_after_save')}</p>
        )}
      </section>
      <section className="panel form-stack sub-form">
        <div>
          <h2 className="rep-sub">{t('voip.hours')}</h2>
          <p className="muted small">{t('voip.hours_hint')}</p>
        </div>
        <div className="voip-hours">
          {f.hours.map((h, i) => (
            <div key={DAY_KEYS[i]} className={`voip-hours-row${h.open ? '' : ' closed'}`}>
              <Switch checked={h.open} onChange={(on) => setDay(i, { open: on })} label={t(`voip.d_${DAY_KEYS[i]}` as MessageKey)} />
              <strong>{t(`voip.d_${DAY_KEYS[i]}` as MessageKey)}</strong>
              {h.open ? (
                <span className="voip-hours-times">
                  <input
                    className="input"
                    type="time"
                    value={h.from}
                    aria-label={t('voip.from_on', { day: t(`voip.d_${DAY_KEYS[i]}` as MessageKey) })}
                    onChange={(e) => setDay(i, { from: e.target.value || '00:00' })}
                  />
                  <span className="muted">–</span>
                  <input
                    className="input"
                    type="time"
                    value={h.to}
                    aria-label={t('voip.to_on', { day: t(`voip.d_${DAY_KEYS[i]}` as MessageKey) })}
                    onChange={(e) => setDay(i, { to: e.target.value || '00:00' })}
                  />
                </span>
              ) : (
                <span className="muted">{t('voip.closed')}</span>
              )}
            </div>
          ))}
        </div>
        {err(errors.hours) ? <p className="field-error">{errors.hours}</p> : null}
        <Field label={t('voip.ring_seconds')} hint={t('voip.ring_seconds_hint')}>
          <span className="input-unit">
            <input className="input" inputMode="numeric" value={f.ring_seconds} onChange={(e) => set('ring_seconds', Math.max(10, Math.min(60, Number(e.target.value.replace(/\D/g, '')) || 10)))} />
            <span>{t('voip.sec')}</span>
          </span>
        </Field>
      </section>
      <section className="panel form-stack sub-form">
        <div>
          <h2 className="rep-sub">{t('voip.messages')}</h2>
          <p className="muted small">{t('voip.messages_hint')}</p>
        </div>
        <Field label={t('voip.language')} hint={t('voip.language_hint')}>
          <select className="input" value={f.language} onChange={(e) => set('language', e.target.value)}>
            {LANGUAGES.map((l) => (
              <option key={l} value={l}>
                {t(`voip.lang_${l.replace('-', '_')}` as MessageKey)}
              </option>
            ))}
          </select>
        </Field>
        <Field label={t('voip.greeting')} hint={t('voip.greeting_hint')}>
          <textarea className="input textarea" rows={2} maxLength={300} value={f.greeting} onChange={(e) => set('greeting', e.target.value)} />
        </Field>
        <Field label={t('voip.closed_prompt')}>
          <textarea className="input textarea" rows={2} maxLength={300} value={f.closed_prompt} onChange={(e) => set('closed_prompt', e.target.value)} />
        </Field>
        <label className="setting-row sms-row">
          <span>
            <strong>{t('voip.voicemail_on')}</strong>
            <span className="muted small">{t('voip.voicemail_on_hint')}</span>
          </span>
          <Switch checked={f.voicemail} onChange={(on) => set('voicemail', on)} label={t('voip.voicemail_on')} />
        </label>
        {f.voicemail ? (
          <Field label={t('voip.voicemail_prompt')}>
            <textarea className="input textarea" rows={2} maxLength={300} value={f.voicemail_prompt} onChange={(e) => set('voicemail_prompt', e.target.value)} />
          </Field>
        ) : null}
        <p className="muted small voip-privacy">
          <Icon name="shield" size={14} /> {t('voip.no_recording')}
        </p>
      </section>
      <div className="row-actions mkt-settings-foot">
        <Button icon="shieldCheck" busy={checking} disabled={!data.account_sid || !data.has_token || !data.token_readable} onClick={() => void verify()}>
          {t('voip.verify')}
        </Button>
        <Button variant="primary" icon="check" busy={busy} onClick={save}>
          {t('common.save')}
        </Button>
      </div>
    </div>
  );
}
